import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { dataCenterAPI, usersAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Database, Download, RefreshCw, Loader2, CalendarRange, Filter } from 'lucide-react';
import { DataCenterKpis } from '@/components/datacenter/DataCenterKpis';
import { GraficoEvolucao, GraficoFunil, GraficoResultado, ListaRanking } from '@/components/datacenter/DataCenterGraficos';
import { TabelaVendedores, TabelaEquipes, TabelaProdutos, TabelaJornada } from '@/components/datacenter/DataCenterTabelas';

const iso = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
  { id: 'hoje', rotulo: 'Hoje', calc: () => { const h = new Date(); return [iso(h), iso(h)]; } },
  { id: '7d', rotulo: '7 dias', calc: () => { const h = new Date(); const i = new Date(); i.setDate(h.getDate() - 6); return [iso(i), iso(h)]; } },
  { id: 'mes', rotulo: 'Mês atual', calc: () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth(), 1)), iso(h)]; } },
  { id: 'mes_ant', rotulo: 'Mês anterior', calc: () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth() - 1, 1)), iso(new Date(h.getFullYear(), h.getMonth(), 0))]; } },
  { id: 'ano', rotulo: 'Ano', calc: () => { const h = new Date(); return [iso(new Date(h.getFullYear(), 0, 1)), iso(h)]; } },
];
const PERFIS = ['ADMIN'];

const DataCenter = () => {
  const { user } = useAuth();
  const podeVer = PERFIS.includes(user?.perfil);
  const [preset, setPreset] = useState('mes');
  const [[ini, fim], setPeriodo] = useState(() => PRESETS[2].calc());
  const [equipeId, setEquipeId] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [exportando, setExportando] = useState(false);

  const params = useMemo(() => ({ data_inicio: ini, data_fim: fim, equipe_id: equipeId || undefined, vendedor_id: vendedorId || undefined }), [ini, fim, equipeId, vendedorId]);

  const carregar = useCallback(async () => {
    if (!podeVer) return;
    setCarregando(true);
    try {
      const res = await dataCenterAPI.resumo(params);
      setDados(res.data);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setCarregando(false);
    }
  }, [params, podeVer]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (user?.perfil !== 'ADMIN' && user?.perfil !== 'BKO') return;
    usersAPI.list().then((r) => setVendedores((r.data || []).filter((u) => u.perfil === 'VENDEDOR' && u.ativo !== false))).catch(() => {});
  }, [user]);

  const aplicarPreset = (p) => { setPreset(p.id); setPeriodo(p.calc()); };

  const exportar = async () => {
    setExportando(true);
    try {
      const res = await dataCenterAPI.exportar(params);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `data_center_${ini}_${fim}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setExportando(false);
    }
  };

  if (!podeVer) return <Navigate to="/dashboard" replace />;

  const equipeSel = equipeId && dados ? dados.equipes.find((e) => e.id === equipeId) : null;
  const vendedoresFiltrados = equipeSel ? vendedores.filter((v) => (equipeSel.membros_ids || []).includes(v.id)) : vendedores;

  return (
    <div className="space-y-5" data-testid="data-center-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Database className="w-6 h-6 text-purple-500" /> Data Center</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Todos os números da operação em um só lugar{dados?.escopo?.restrito ? ' — mostrando apenas a sua equipe' : ''}.{dados?.gerado_em ? ` Atualizado ${new Date(dados.gerado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={carregar} disabled={carregando} data-testid="dc-atualizar"><RefreshCw className={`w-4 h-4 mr-1.5 ${carregando ? 'animate-spin' : ''}`} /> Atualizar</Button>
          <Button size="sm" onClick={exportar} disabled={exportando || !dados} className="bg-purple-600 hover:bg-purple-700" data-testid="dc-exportar">{exportando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Exportar Excel</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 dark:border-purple-900/50 bg-white dark:bg-[#15082a] p-3" data-testid="dc-filtros">
        <CalendarRange className="w-4 h-4 text-purple-500" />
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => aplicarPreset(p)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${preset === p.id ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-purple-900/30 text-gray-600 dark:text-purple-200 hover:bg-gray-200 dark:hover:bg-purple-900/50'}`} data-testid={`dc-preset-${p.id}`}>{p.rotulo}</button>
        ))}
        <input type="date" value={ini} onChange={(e) => { setPreset(''); setPeriodo([e.target.value, fim]); }} className="rounded-md border bg-transparent px-2 py-1 text-xs dark:border-purple-800 dark:[color-scheme:dark]" data-testid="dc-data-inicio" />
        <span className="text-xs text-gray-400">até</span>
        <input type="date" value={fim} onChange={(e) => { setPreset(''); setPeriodo([ini, e.target.value]); }} className="rounded-md border bg-transparent px-2 py-1 text-xs dark:border-purple-800 dark:[color-scheme:dark]" data-testid="dc-data-fim" />
        <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-purple-900/50" />
        <Filter className="w-4 h-4 text-purple-500" />
        <select value={equipeId} onChange={(e) => { setEquipeId(e.target.value); setVendedorId(''); }} className="rounded-md border bg-transparent px-2 py-1 text-xs dark:border-purple-800 dark:bg-[#15082a]" data-testid="dc-filtro-equipe">
          <option value="">Todas as equipes</option>
          {(dados?.equipes || []).map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        {vendedores.length > 0 && (
          <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)} className="rounded-md border bg-transparent px-2 py-1 text-xs dark:border-purple-800 dark:bg-[#15082a]" data-testid="dc-filtro-vendedor">
            <option value="">Todos os vendedores</option>
            {vendedoresFiltrados.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
          </select>
        )}
      </div>

      {carregando && !dados ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
      ) : dados && (
        <div className={`space-y-4 transition-opacity ${carregando ? 'opacity-60' : ''}`}>
          <DataCenterKpis k={dados.kpis} />
          <div className="grid gap-4 xl:grid-cols-3">
            <GraficoEvolucao dados={dados.evolucao} />
            <GraficoResultado k={dados.kpis} />
          </div>
          <div className="grid gap-4 xl:grid-cols-3">
            <TabelaVendedores itens={dados.producao_vendedores} mesMeta={dados.periodo?.mes_referencia_meta} />
            <GraficoFunil funil={dados.funil} />
          </div>
          <div className="grid gap-4 xl:grid-cols-3">
            <TabelaEquipes itens={dados.producao_equipes} />
            <TabelaProdutos itens={dados.produtos} />
            <ListaRanking titulo="Motivos de cancelamento" descricao="Por que as vendas caíram" itens={dados.motivos_cancelamento} cor="#ef4444" testid="dc-motivos-cancelamento" />
          </div>
          <div className="grid gap-4 xl:grid-cols-3">
            <TabelaJornada itens={dados.jornada} />
            <div className="grid gap-4">
              <ListaRanking titulo="Modalidades" descricao="TT, Proposta, Base, Prospect" itens={dados.modalidades} cor="#06b6d4" testid="dc-modalidades" />
              <ListaRanking titulo="Motivos de devolução" descricao="O que o BKO mais devolve" itens={dados.motivos_devolucao} cor="#f97316" testid="dc-motivos-devolucao" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataCenter;
